import {
  CopyToClipboardButton,
  ErrorBox,
  WarnBox,
} from "@gearbox-protocol/deploy-tools-ui";
import type {
  RouterGraph,
  RouterResult,
  RouterTestResult,
} from "@gearbox-protocol/router-graph-ui";
import { RouterPath, usePathSelector } from "@gearbox-protocol/router-graph-ui";
import type { OnchainSDK } from "@gearbox-protocol/sdk/onchain";
import { type FC, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { Address } from "viem";
import { useUnpausedForkStatus } from "../../context";
import { CreditManagerSelector } from "./CreditManagerSelector";
import { TokenSelector } from "./TokenSelector";

interface RouterTestsReportProps {
  sdk: OnchainSDK;
  routerTestResults: RouterTestResult[];
}

export const RouterTestsReport: FC<RouterTestsReportProps> = ({
  sdk,
  routerTestResults,
}) => {
  const [searchParams] = useSearchParams();
  const { executionId, alias } = useUnpausedForkStatus();

  // Read default values from query string
  const defaultCreditManager = searchParams.get("creditManager");
  const defaultTokenIn = searchParams.get("tokenIn");
  const defaultTokenOut = searchParams.get("tokenOut");

  const defaultValue =
    defaultCreditManager && defaultTokenIn && defaultTokenOut
      ? {
          creditManager: defaultCreditManager as `0x${string}`,
          tokenIn: defaultTokenIn as `0x${string}`,
          tokenOut: defaultTokenOut as `0x${string}`,
        }
      : undefined;

  const {
    creditManagers,
    creditManager,
    onChangeCreditManager,
    graph,
    tokensIn,
    tokenIn,
    onChangeTokenIn,
    tokensOut,
    tokenOut,
    onChangeTokenOut,
    result,
  } = usePathSelector(sdk, routerTestResults, defaultValue);

  const permalink = useMemo(() => {
    const urlObj = new URL(window.location.href);
    if (creditManager) {
      urlObj.searchParams.set("creditManager", creditManager);
    }
    if (tokenIn) {
      urlObj.searchParams.set("tokenIn", tokenIn);
    }
    if (tokenOut) {
      urlObj.searchParams.set("tokenOut", tokenOut);
    }

    return urlObj.toString().replaceAll(alias ?? "???", executionId);
  }, [executionId, alias, creditManager, tokenIn, tokenOut]);

  return (
    <div className="flex flex-col gap-4 mt-4">
      <h3>Router Tests Report</h3>
      <div className="flex flex-row gap-4">
        <CreditManagerSelector
          sdk={sdk}
          values={creditManagers}
          value={creditManager}
          label="Credit Manager"
          onChange={onChangeCreditManager}
        />
        <TokenSelector
          values={tokensIn}
          value={tokenIn}
          label="Token In"
          onChange={onChangeTokenIn}
        />
        <TokenSelector
          values={tokensOut}
          value={tokenOut}
          label="Token Out"
          onChange={onChangeTokenOut}
        />
        <CopyToClipboardButton text={permalink} className="ml-auto" />
      </div>

      <RouterResultView
        sdk={sdk}
        creditManager={creditManager}
        graph={graph}
        result={result}
      />
    </div>
  );
};

interface RouterResultViewProps {
  sdk: OnchainSDK;
  creditManager?: Address;
  graph?: RouterGraph;
  result?: RouterResult;
}

export const RouterResultView: FC<RouterResultViewProps> = props => {
  const { sdk, creditManager, graph, result } = props;
  if (!result?.pathFound) {
    return <ErrorBox>No path found for selected tokens</ErrorBox>;
  }
  if (!graph) {
    return <ErrorBox>No graph found for selected credit manager</ErrorBox>;
  }
  if (!creditManager) {
    return <ErrorBox>No credit manager selected</ErrorBox>;
  }
  return (
    <>
      {!result.pathExecuted && <WarnBox>Path not executed</WarnBox>}
      <div className="self-stretch h-[640px]">
        <RouterPath
          sdk={sdk}
          creditManager={creditManager}
          graph={graph}
          result={result}
        />
      </div>
    </>
  );
};
